import { Controller } from '@hotwired/stimulus'

const EXTREME_LOSS_THRESHOLD = 0.85;

export default class SellingOfferFormController extends Controller {
  static targets = [
    'priceInput',
    'warningDiscount',
    'higherPriceWarning',
    'capitalGainPercent',
    'warningMinCap',
    'warningMaxCap',
    'invalidFeedback',
    'extremeLossWarning',
    'extremeLossCheckbox',
    'extremeLossCheckboxContainer',
    'submitButton',
    'capitalLoss',
    'capitalLossPercent',
    'automaticWarning',
    'automaticWarningEqual',
    'automaticWarningBelow',
    'automaticWarningAbove',
    'automaticWarningPercent',
    'automaticWarningFieldset',
  ];

  static values = {
    crd: Number,
    maxPrice: Number,
  };

  connect() {
    this._debounceTimer = null;
    this._currencyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
    this._maxPrice = this.maxPriceValue;
    this._update();
  }

  onPriceChange() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._update(), 300);
  }

  onExtremeLossCheckboxChange() {
    this._updateSubmitButton();
  }

  _update() {
    const price = parseFloat(this.priceInputTarget.value) || 0;

    this._hideAllWarnings();

    if (price < 1) {
      this.priceInputTarget.classList.add('is-invalid');
      this.warningMinCapTarget.classList.remove('d-none');
      this.invalidFeedbackTarget.classList.add('d-block');
      this.extremeLossCheckboxContainerTarget.classList.add('d-none');
    } else if (price > this._maxPrice) {
      this.priceInputTarget.classList.add('is-invalid');
      this.warningMaxCapTarget.classList.remove('d-none');
      this.invalidFeedbackTarget.classList.add('d-block');
    } else if (price > this.crdValue) {
      const percent = ((price - this.crdValue) / this.crdValue * 100).toFixed(0);
      this.capitalGainPercentTarget.textContent = `${percent}%`;
      this.higherPriceWarningTarget.classList.remove('d-none');
    } else if (price < this.crdValue) {
      this.priceInputTarget.classList.remove('is-invalid');
      const loss = this.crdValue - price;
      const percent = ((this.crdValue - price) / this.crdValue * 100).toFixed(0);
      const extremeLoss = this._isExtremeLoss(price);
      this.capitalLossTargets.forEach(el => el.textContent = this._currencyFormatter.format(loss));
      this.capitalLossPercentTargets.forEach(el => el.textContent = `${percent}%`);
      extremeLoss ?
        this.extremeLossWarningTarget.classList.remove('d-none') :
        this.warningDiscountTarget.classList.remove('d-none');
    }

    this._updateAutomaticWarning(price);
    this._updateSubmitButton();
  }

  _updateAutomaticWarning(price) {
    this.automaticWarningTarget.classList.add('d-none');
    this.automaticWarningEqualTarget.classList.add('d-none');
    this.automaticWarningBelowTarget.classList.add('d-none');
    this.automaticWarningAboveTarget.classList.add('d-none');

    const percentFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

    if (price < 1) {
      this.automaticWarningTarget.classList.remove('d-none');
    } else if (price === this.crdValue) {
      this.automaticWarningEqualTarget.classList.remove('d-none');
    } else if (price < this.crdValue) {
      const percent = percentFormatter.format((this.crdValue - price) / this.crdValue * 100);
      this.automaticWarningPercentTargets.forEach(el => el.textContent = percent);
      this.automaticWarningBelowTarget.classList.remove('d-none');
    } else {
      const percent = percentFormatter.format((price - this.crdValue) / this.crdValue * 100);
      this.automaticWarningPercentTargets.forEach(el => el.textContent = percent);
      this.automaticWarningAboveTarget.classList.remove('d-none');
    }
  }

  _hideAllWarnings() {
    this.warningDiscountTarget.classList.add('d-none');
    this.extremeLossWarningTarget.classList.add('d-none');
    this.higherPriceWarningTarget.classList.add('d-none');
    this.warningMaxCapTarget.classList.add('d-none');
    this.warningMinCapTarget.classList.add('d-none');
    this.invalidFeedbackTarget.classList.remove('d-block');
    this.priceInputTarget.classList.remove('is-invalid');
    this.extremeLossCheckboxContainerTarget.classList.add('d-none');
    this.extremeLossCheckboxTarget.required = false;
  }

  _updateSubmitButton() {
    const price = parseFloat(this.priceInputTarget.value) || 0;
    const outOfBounds = price < 1 || price > this._maxPrice;
    const needsExtremeLossConsent = !outOfBounds && this._isExtremeLoss(price);

    this.extremeLossCheckboxContainerTarget.classList.toggle('d-none', !needsExtremeLossConsent);
    this.extremeLossCheckboxTarget.required = needsExtremeLossConsent;

    this.submitButtonTarget.disabled =
      outOfBounds ||
      (needsExtremeLossConsent && !this.extremeLossCheckboxTarget.checked);
    this.automaticWarningFieldsetTarget.disabled = outOfBounds;
    this.automaticWarningFieldsetTarget.classList.toggle('opacity-25', outOfBounds);
  }

  _isExtremeLoss(price) {
    return price < this.crdValue * EXTREME_LOSS_THRESHOLD;
  }
}
